import isNumber from 'lodash/isNumber';

import { Service, State } from './types';

export const BrowserStorageService: Service = new (class {
  private storageKey = 'gaf-pp-storage';

  private getStorage = (forceSessionStorage = false): Storage => {
    return forceSessionStorage ? sessionStorage : localStorage;
  };

  private state = {
    get: (forceSessionStorage = false): State => {
      try {
        const state = this.getStorage(forceSessionStorage).getItem(this.storageKey);

        return state ? JSON.parse(state) : {};
      } catch {
        return {};
      }
    },

    set: (newState: State, forceSessionStorage = false) => {
      const currentState = this.state.get(forceSessionStorage);
      const storage = this.getStorage(forceSessionStorage);

      storage.setItem(this.storageKey, JSON.stringify({ ...currentState, ...newState }));
    },

    removeItem: (itemKey: keyof State, forceSessionStorage = false) => {
      const state = this.state.get(forceSessionStorage);

      state[itemKey] = undefined;
      this.state.set(state, forceSessionStorage);
    },
  };

  readonly removeItems: Service['removeItems'] = (
    itemKeys: (keyof State)[],
    forceSessionStorage = false,
  ) => {
    const state = this.state.get(forceSessionStorage);

    itemKeys.forEach(itemKey => (state[itemKey] = undefined));
    this.state.set(state, forceSessionStorage);
  };

  readonly portalAccessToken: Service['portalAccessToken'] = {
    get: (forceSessionStorage = false) => {
      return this.state.get(forceSessionStorage).portalAccessToken;
    },
    set: (value: State['portalAccessToken'], forceSessionStorage = false) => {
      this.state.set({ portalAccessToken: value }, forceSessionStorage);
    },
    remove: (forceSessionStorage = false) => {
      this.state.removeItem('portalAccessToken', forceSessionStorage);
    },
  };

  readonly xReCaptchaId: Service['xReCaptchaId'] = {
    get: (forceSessionStorage = true) => {
      return this.state.get(forceSessionStorage).xReCaptchaId;
    },
    set: (value: State['xReCaptchaId'], forceSessionStorage = true) => {
      this.state.set({ xReCaptchaId: value }, forceSessionStorage);
    },
    remove: (forceSessionStorage = true) => {
      this.state.removeItem('xReCaptchaId', forceSessionStorage);
    },
  };

  readonly customerId: Service['customerId'] = {
    get: () => this.state.get().customerId,
    set: (value: State['customerId']) => this.state.set({ customerId: value }),
    remove: () => this.state.removeItem('customerId'),
  };

  readonly userCompanyId: Service['userCompanyId'] = {
    get: () => this.state.get().userCompanyId,
    set: (value: State['userCompanyId']) => this.state.set({ userCompanyId: value }),
    remove: () => this.state.removeItem('userCompanyId'),
  };

  readonly leadsPassingProgram: Service['leadsPassingProgram'] = {
    get: () => Boolean(this.state.get().leadsPassingProgram),
    set: () => this.state.set({ leadsPassingProgram: true }),
    remove: () => this.state.removeItem('leadsPassingProgram'),
  };

  readonly getCertified: Service['getCertified'] = {
    get: () => Boolean(this.state.get().getCertified),
    set: () => this.state.set({ getCertified: true }),
    remove: () => this.state.removeItem('getCertified'),
  };

  readonly loggedOutDueToInactivity: Service['loggedOutDueToInactivity'] = {
    get: () => Boolean(this.state.get(true).loggedOutDueToInactivity),
    set: () => this.state.set({ loggedOutDueToInactivity: true }, true),
    remove: () => this.state.removeItem('loggedOutDueToInactivity', true),
  };

  readonly postAuthRedirect: Service['postAuthRedirect'] = {
    get: () => this.state.get().postAuthRedirect,
    set: (route?: State['postAuthRedirect']) => {
      const { pathname, search } = route || {};

      this.state.set({
        postAuthRedirect: {
          pathname: pathname || window.location.pathname,
          search: search || window.location.search,
        },
      });
    },
    remove: () => this.state.removeItem('postAuthRedirect'),
  };

  readonly lastActivity: Service['lastActivity'] = {
    get: () => {
      const timestamp = this.state.get().lastActivity;

      return timestamp && isNumber(Number(timestamp)) ? Number(timestamp) : null;
    },
    set: () => this.state.set({ lastActivity: Date.now() }),
    remove: () => this.state.removeItem('lastActivity'),
  };
})();
